import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootStoreType } from 'src/types/store';
import { checkFormValidity } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import { EgonRouteType } from 'src/types/egonRoute';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { setLocationParams } from 'src/resources/location/reducer';
import {
    clearEgonRouteDetail,
    createEgonRouteDetailFetch,
    deleteEgonRouteDetailFetch,
    getEgonRouteDetailFetch,
    updateEgonRouteDetailFetch,
} from 'src/resources/egonRouteDetail/reducer';
import { getEgonDeliveriesFetch } from 'src/resources/egonDeliveries/reducer';
import TopBlock from './TopBlock';
import Detail from './Detail';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType } from 'src/types/global';
import { validateEgonRouteForm } from 'src/utils/egonRoutes';

const initData: EgonRouteType = { ENABLED: 1, COUNTRY_CODES: [] };

const EgonRouteDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idEgonRoute } = useParams();
    const newRoute = !idEgonRoute;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.egonRouteDetail?.data);
    const loading = useSelector((state: RootStoreType) => state.egonRouteDetail?.loading);
    const deleted = useSelector((state: RootStoreType) => state.egonRouteDetail?.deleted);
    const created = useSelector((state: RootStoreType) => state.egonRouteDetail?.created);
    const deliveries = useSelector((state: RootStoreType) => state.egonDeliveries.data);
    const deliveriesLoading = useSelector((state: RootStoreType) => state.egonDeliveries.loading);
    const deliveriesFailed = useSelector((state: RootStoreType) => state.egonDeliveries.error);
    const shipperCodes = useSelector((state: RootStoreType) =>
        state.constantsCodes.data.shipperCodes
            ? [...state.constantsCodes.data.shipperCodes].sort((a, b) => a.label.localeCompare(b.label))
            : []
    );
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes || []);

    const [editData, setEditData] = useState<EgonRouteType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('EGON_ROUTE.CREATE');
    const permissionUpdate = useCheckPermission('EGON_ROUTE.UPDATE');
    const disableEdit = newRoute ? !permissionCreate : !permissionUpdate;

    const backUrl = '/admin/egon-routes/';

    useEffect(() => {
        if (idEgonRoute) {
            const displayId = data?.ID !== undefined ? data.ID : idEgonRoute;
            dispatch(setLocationParams({ name: translate('egonRouteDetail.title', { idEgonRoute: displayId }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('egonRouteDetail.titleNew'), backUrl }));
        }
    }, [idEgonRoute, data?.ID]);

    useEffect(() => {
        dispatch(getEgonDeliveriesFetch());
    }, []);

    useEffect(() => {
        if (idEgonRoute) {
            dispatch(getEgonRouteDetailFetch(idEgonRoute));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearEgonRouteDetail());
        };
    }, [idEgonRoute]);

    useEffect(() => {
        if (data) {
            setEditData({ ...data });
        }
    }, [data]);

    useEffect(() => {
        if (deleted || created) {
            navigate('/admin/egon-routes');
        }
    }, [deleted, created]);

    const patch = (changes: EgonRouteType, clearErrorKeys: string[] = []) => {
        setEditData((prev) => ({ ...prev, ...changes }));
        setEditErrors((prev) => {
            const next = { ...prev };
            clearErrorKeys.forEach((key) => delete next[key]);
            return next;
        });
    };

    const onChangeShipper = (value: string | undefined) => {
        patch(
            {
                SHIPPER_CODE: value,
                SERVICE_CODE_VALUE: undefined,
                ID_SERVICE_CODE: undefined,
                COUNTRY_CODES: [],
                COUNTRY_CODE: undefined,
            },
            ['SERVICE_CODE_VALUE', 'COUNTRY_CODES', 'COUNTRY_CODE']
        );
    };

    const onChangeService = (value: string | undefined) => {
        const service = serviceCodes.find((code) => code.value === value);
        const allowedCountries = service?.COUNTRY_SERVICE_CODES || [];
        patch(
            {
                SERVICE_CODE_VALUE: value,
                ID_SERVICE_CODE: service?.id,
                // keep only countries that the new service supports
                COUNTRY_CODES: (editData.COUNTRY_CODES || []).filter((code) => allowedCountries.includes(code)),
                COUNTRY_CODE:
                    editData.COUNTRY_CODE && allowedCountries.includes(editData.COUNTRY_CODE)
                        ? editData.COUNTRY_CODE
                        : undefined,
            },
            ['SERVICE_CODE_VALUE']
        );
    };

    const onChangeDelivery = (deliveryId: number | undefined) => {
        const delivery = (deliveries || []).find((item) => item.id === deliveryId);
        patch(
            {
                EGON_DELIVERY_ID: deliveryId,
                EGON_DELIVERY_NAME: delivery ? delivery.name : null,
                EGON_TRANSFER_TYPE: delivery ? delivery.transferType : null,
            },
            ['EGON_DELIVERY_ID']
        );
    };

    const handleSave = () => {
        const newFormErrors = { ...checkFormValidity(formRef), ...validateEgonRouteForm(editData, newRoute) };

        if (!Object.keys(newFormErrors).length && editData.ID_SERVICE_CODE && editData.EGON_DELIVERY_ID) {
            const common = {
                ID_SERVICE_CODE: editData.ID_SERVICE_CODE,
                EGON_DELIVERY_ID: editData.EGON_DELIVERY_ID,
                EGON_DELIVERY_NAME: editData.EGON_DELIVERY_NAME ?? null,
                EGON_TRANSFER_TYPE: editData.EGON_TRANSFER_TYPE ?? null,
                ENABLED: editData.ENABLED ? 1 : 0,
                NOTE: editData.NOTE || null,
            };
            if (newRoute) {
                dispatch(createEgonRouteDetailFetch({ ...common, COUNTRY_CODES: editData.COUNTRY_CODES || [] }));
            } else if (idEgonRoute && editData.COUNTRY_CODE) {
                dispatch(
                    updateEgonRouteDetailFetch({ id: idEgonRoute, data: { ...common, COUNTRY_CODE: editData.COUNTRY_CODE } })
                );
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const handleDelete = () => {
        setConfirmParams({
            title: translate('egonRouteDetail.deleteConfirm'),
            action: () => {
                if (idEgonRoute) dispatch(deleteEgonRouteDetailFetch(idEgonRoute));
            },
        });
    };

    return (
        <div data-testid="egon-route-detail">
            <TopBlock newRoute={newRoute} disableEdit={disableEdit} handleSave={handleSave} handleDelete={handleDelete} />
            <form ref={formRef}>
                {loading && <Loading />}
                <Detail
                    editData={editData}
                    editErrors={editErrors}
                    newRoute={newRoute}
                    disableEdit={disableEdit}
                    shipperCodes={shipperCodes}
                    serviceCodes={serviceCodes}
                    deliveries={deliveries}
                    deliveriesFailed={deliveriesFailed}
                    deliveriesLoading={deliveriesLoading}
                    onChangeShipper={onChangeShipper}
                    onChangeService={onChangeService}
                    onChangeCountries={(value) => patch({ COUNTRY_CODES: value }, ['COUNTRY_CODES'])}
                    onChangeCountry={(value) => patch({ COUNTRY_CODE: value }, ['COUNTRY_CODE'])}
                    onChangeDelivery={onChangeDelivery}
                    onChangeField={(key, value) => patch({ [key]: value })}
                />
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </div>
    );
};

export default EgonRouteDetailContainer;
